import { TFunction } from "i18next";
import { Player, Team } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";
import { SKIP_RECIPIENT_ID } from "../../constants";

// PMA-COCK-165/166: a synthetic Player the "skip" control sends through the
// existing onRecipientSelect callback — see SKIP_RECIPIENT_ID's doc comment
// in constants.ts for why this avoids a new prop end-to-end.
const SKIP_RECIPIENT: Player = {
  id: SKIP_RECIPIENT_ID,
  full_name: "",
  jersey_number: 0,
  position: "",
};

/** Actions whose recipient step is optional — dispatches with or without a
 * pick, so a skip control renders alongside the roster grid and Cancel.
 * BlockedShot removed by PMA-COCK-165's field-destination rework (client
 * wave 2026-09-07) — it no longer reaches this panel; the blocker is now
 * picked by tapping the opponent under the shot's destination tap. */
const OPTIONAL_RECIPIENT_ACTIONS: ReadonlySet<string> = new Set(["Foul"]);

interface RecipientSelectionPanelProps {
  team?: Team;
  eligiblePlayers?: Player[];
  selectedAction: string | null;
  // PMA-COCK-147: renamed from `isSubmitting` -- an ack-latency "syncing"
  // state must never disable these buttons (that silently swallows a
  // legitimate tap); only a genuine lock (cockpitLocked) may.
  disabled: boolean;
  onRecipientSelect: (player: Player) => void;
  onCancel: () => void;
  t: TFunction<"logger">;
}

const RecipientSelectionPanel = ({
  team,
  eligiblePlayers,
  selectedAction,
  disabled,
  onRecipientSelect,
  onCancel,
  t,
}: RecipientSelectionPanelProps) => {
  if (!team) {
    return null;
  }

  // PMA-COCK-166: "¿A quién?" is the client's exact wording for the fouled-
  // player prompt. Pass, Duel, and Penalty keep the generic heading —
  // unchanged. BlockedShot's own "¿Quién bloqueó?" heading (PMA-COCK-171)
  // was retired by PMA-COCK-165's field-destination rework (client wave
  // 2026-09-07) — this panel is no longer reached for BlockedShot at all.
  const heading =
    selectedAction === "Foul" ? (
      t("recipientFoulPrompt", "¿A quién?")
    ) : (
      <>
        {t("selectRecipient")} - {selectedAction}
      </>
    );

  const isOptional =
    !!selectedAction && OPTIONAL_RECIPIENT_ACTIONS.has(selectedAction);
  const skipLabel = t("recipientSkipFoulVictim", "Sin víctima");

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-4">{heading}</h2>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
        {(eligiblePlayers ?? team.players).map((player) => (
          <button
            key={player.id}
            onClick={() => onRecipientSelect(player)}
            disabled={disabled}
            data-testid={`recipient-card-${player.id}`}
            className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors ${
              disabled
                ? "bg-purple-300 text-white cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white"
            }`}
          >
            <div className="text-3xl font-bold">{player.jersey_number}</div>
            <div className="text-xs text-center px-1 mt-1 line-clamp-2">
              {formatDisplayName(player.full_name)}
            </div>
          </button>
        ))}
        {isOptional && (
          <button
            onClick={() => onRecipientSelect(SKIP_RECIPIENT)}
            disabled={disabled}
            data-testid="recipient-skip"
            className={`aspect-square rounded-lg flex items-center justify-center font-medium text-xs text-center px-1 transition-colors ${
              disabled
                ? "bg-slate-300 text-white cursor-not-allowed"
                : "bg-slate-500 hover:bg-slate-600 text-white"
            }`}
          >
            {skipLabel}
          </button>
        )}
        <button
          onClick={onCancel}
          className="aspect-square bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg flex items-center justify-center font-medium"
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
};

export default RecipientSelectionPanel;
